import { Component, OnInit, Input,  Signal } from '@angular/core';
import { Subject } from 'rxjs';
import { Observable, Subscription } from 'rxjs';
import { IPassLayoutContext } from '../../interfaces/pass-layout/pass-layout-context';
import { IKeyValuePairs } from '../../interfaces/pass-layout/assigned-slots';
import { IAssignedSlots } from '../../interfaces/pass-layout/assigned-slots';
import { ILayoutSlot } from '../../interfaces/pass-layout/layout-slot';
import { ILayoutSection } from '../../interfaces/pass-layout/layout-section';
import { ILayoutFieldKey }  from '../../interfaces/pass-layout/layout-field-key';
import { ITemplateLayoutSection } from '../../interfaces/pass-layout/template-layout-section';
import { DigitalWalletPlatform } from '../../enums/digital-wallet-platform';

@Component({
  selector: 'app-pass-layout',
  standalone: false,
  templateUrl: './pass-layout.component.html',
  styleUrl: './pass-layout.component.css'
})
export class PassLayoutComponent  implements OnInit {

  @Input() messageObservable!: Observable<IPassLayoutContext>;
  @Input({ required: true }) onNotify!: Signal<(isFieldDetailsShown: boolean, fieldKey: string) => void>;
  private subscription!: Subscription;

  platform!: DigitalWalletPlatform;
  fieldKeys: ILayoutFieldKey[] = [];
  templateLayout: ITemplateLayoutSection[] = [];
  layoutPages: number[] = [];
  assignedSlots: IKeyValuePairs = {} ; 

  selectedFieldKey!: string;

  selectedSortType: string = 'alphabetical';
  isSortedByFieldOrder: boolean = false;

  constructor(){
  }

  ngOnInit(): void {
    this.subscription = this.messageObservable.subscribe(msg => {
      if (msg) {
        this.platform = msg.platform;
        this.templateLayout = msg.layoutSections;
        this.fieldKeys = msg.fieldKeys;
        this.assignedSlots = msg.assignedSlots;

        // Calculate the number of pages
        this.templateLayout.forEach(item => {
          if (!this.layoutPages.includes(item.pageNum)) this.layoutPages.push(item.pageNum);
        });
        this.layoutPages.sort();

        // Mark slots taken by field keys for an existing layout
        this.setAssignedSlots();
      }
    });
  }  

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }


  // Find out already assigned slots, so these slots will have fieldKeys in them
  setAssignedSlots() {
    if (this.assignedSlots) {
      for (const [slotId, fieldKey] of Object.entries(this.assignedSlots)) {
        this.templateLayout.forEach(item => {
          let slot = item.slotList.find(slot => slot.slotId === slotId);
          if (slot) {
            // console.log(`platform: ${this.platform}`,`Assigned slot: ${slot.slotId} to fieldKey: ${fieldKey}`);
            slot.fieldKey = fieldKey;
            this.removeItemFromAvailableList(fieldKey);
          }
        })
      }
    }

    // If a specific section requires more than one column, then we need to rearrange slots accordingly
    this.rearrangeSlots();
  }  

  // This method is needed to properly display one or multiple sections across the page
  rearrangeSlots() {
    this.templateLayout.forEach(section => {
      const result: ILayoutSlot [][] = [];
      for (let i = 0; i < section.slotList.length; i += section.numOfCols) {
        result.push(section.slotList.slice(i, i + section.numOfCols));
      }
      section.slots = result;
    })
  }  

  // Find out assigned slots
  getAssignedSlots(): [DigitalWalletPlatform, IKeyValuePairs] {
    // console.log('child - getAssignedSlots', this.platform);
    return [this.platform, this.assignedSlots];
  }
  
  onAvailableRowClick(item: any) {
    if (item) {
      if (item.fieldKey) {
        if (this.selectedFieldKey == item.fieldKey) {
          this.selectedFieldKey = '';
          // Call the function inside the signal
          this.onNotify()(false, item.fieldKey); 
        } else {
          this.selectedFieldKey = item.fieldKey;
          // Call the function inside the signal
          this.onNotify()(true, item.fieldKey); 
        }
      }
    }
  }

  onDragStart(event: DragEvent, fieldKey: string) {
    //event.dataTransfer?.setData('text/plain', JSON.stringify(item));
    event.dataTransfer?.setData('text/plain', fieldKey);
    (event.target as HTMLElement).style.opacity = '0.4';
  }  

  onDragOver(event: DragEvent) {
    event.preventDefault(); // Allow dropping
  }

  onDragEnd(event: DragEvent) {
    (event.target as HTMLElement).style.opacity = '1';
    (event.target as HTMLElement).classList.remove('over');
  }

  onDragEnter(event: DragEvent) {
    // console.log('drag enter', event);
    (event.target as HTMLElement).classList.add('over');
  }

  onDragLeave(event: DragEvent) {
    // console.log('drag leave', event);
    (event.target as HTMLElement).classList.remove('over');
  }

  onDrop(event: DragEvent) {
    // this.onNotify()(false, ''); 
    // console.log("dropped event:", event);
    event.preventDefault();
    const fieldKey = event.dataTransfer?.getData('text/plain');
    // console.log("dropped item:", fieldKey);
    if (fieldKey) {

      // Find target slotId; it might be covered by previously dropped item,
      // so we may need to drill up to its parent element
      let slotId : string | undefined = (event.target as HTMLElement).id;
      if (!slotId) {
        slotId = (event.target as HTMLElement).parentElement?.id;
      }
      if (!slotId) {
        slotId = (event.target as HTMLElement).offsetParent?.id;
      }      
      if (!slotId) return;
 
      // This item might have been already assigned to another slot,
      // so we need to remove it from there. So, let us find a slot (if any)
      // that contains this fieldKey

      this.templateLayout.forEach(section => {
        let previousSlot = section.slotList.find(slot => slot.fieldKey === fieldKey);
        if (previousSlot) {
          previousSlot.fieldKey = null;
          for (const slotId in this.assignedSlots) {
              if (this.assignedSlots[slotId] === fieldKey) {
                  delete this.assignedSlots[slotId];
              }
          }
        }
      })
 
      // Find the target slot by slot id:
      this.templateLayout.forEach(section => {
        let targetSlot = section.slotList.find(slot => slot.slotId === slotId);
        if (targetSlot) {

          const isSlotInsertable: boolean = this.isParentSectionInsertable(targetSlot.slotId);
 
          // If the target slot already has an item and the parent section IS insertable, 
          // then we need to move all already assigned field keys down within the parent section.
          if (targetSlot.fieldKey && isSlotInsertable) {
            this.insertFieldKeyIntoSection(section.sectionName, slotId, fieldKey);
            // console.log('this.templateLayout', this.templateLayout);
            // console.log('this.assignedSlots', this.assignedSlots);
          } else {

            if (targetSlot.fieldKey) {
              this.returnItemToAvailableList(targetSlot.fieldKey);
            }
  
            // Assign the fieldKey to the slot
            targetSlot.fieldKey = fieldKey;
            this.assignedSlots[targetSlot.slotId] = fieldKey;

          }

        }
      })

      // "Remove" from list of available items. Not physically remove but
      // set "inUse" flag
      this.removeItemFromAvailableList(fieldKey);
      
      (event.target as HTMLElement).classList.remove('over');

      // console.log("dropped eventTarget ID:", slotId);
    }
  }

  onDropToAvailable(event: DragEvent) {
    // this.onNotify()(false, ''); 
    event.preventDefault();
    const fieldKey = event.dataTransfer?.getData('text/plain');
    // console.log("onDropToAvailable event:", event);
    // console.log("onDropToAvailable itemId:", fieldKey);
    if (fieldKey) {

      this.templateLayout.forEach(item => {
        let previousSlot = item.slotList.find(slot => slot.fieldKey === fieldKey);
        if (previousSlot) {
          previousSlot.fieldKey = null;
        }
      })

      this.returnItemToAvailableList(fieldKey);
    }
  }

  private returnItemToAvailableList(fieldKey: string | null | undefined) :void {
    if (fieldKey) {
      let returnedItem = this.fieldKeys.find(item => item.fieldKey === fieldKey);
      if (returnedItem) {
        returnedItem.inUse = false;
      } 
      for (const slotId in this.assignedSlots) {
          if (this.assignedSlots[slotId] === fieldKey) {
              delete this.assignedSlots[slotId];
          }
      }
    }
  }

  private removeItemFromAvailableList(fieldKey: string | null | undefined) :void {
    if (fieldKey) {
      let removedItem = this.fieldKeys.find(item => item.fieldKey === fieldKey);
      if (removedItem) {
        removedItem.inUse = true;
      } 
    }
  }

  onToggleChange(event: boolean) {
    if (event) {
      // Sort out field keys by field order
      this.fieldKeys.sort((a, b) => (a.fieldOrder ? a.fieldOrder : 0) - (b.fieldOrder ? b.fieldOrder : 0));
    } else {
      // Sort out alphbetically
      this.fieldKeys.sort((a, b) => a.fieldKey.localeCompare(b.fieldKey));
    }
  }

  // Find out whether the parent seection for a given slot allows to "insert" field keys
  isParentSectionInsertable(slotId: string): boolean {
    let isInsertable: boolean = false;
    this.templateLayout.forEach(section => {
      let slot = section.slotList.find(slot => slot.slotId === slotId);
      if (slot && section.insertable) {
        isInsertable = true;
      }
    })
    return isInsertable;
  }

  // insertFieldKeyIntoSection(sectionName: string, targetSlot: string, newFieldKey: string) {
  //   this.templateLayout.forEach(section => {
  //     if (section.sectionName == sectionName) {
  //       const index = section.slotList.findIndex(slot => slot.slotId === targetSlot);
  //       if (index === -1) return section.slotList; // Slot not found

  //       if (section.slotList[index].fieldKey === "") {
  //         // Slot is free, just assign the field key
  //         section.slotList[index].fieldKey = newFieldKey;
  //         this.assignedSlots[targetSlot] = newFieldKey;
  //       } else {
  //         // Is the last slot within the section already taken?  We will need to send it back to the Available later on.
  //         const maxIndex = section.slotList.length - 1;
  //         const lastFieldKey = section.slotList[maxIndex].fieldKey;
  //         // Slot is taken, shift field keys down from the target slot
  //         for (let i = section.slotList.length - 1; i > index; i--) {
  //           section.slotList[i].fieldKey = section.slotList[i - 1].fieldKey;
  //           this.assignedSlots[section.slotList[i].slotId] = section.slotList[i - 1].fieldKey || '';
  //         }
  //         section.slotList[index].fieldKey = newFieldKey;
  //         this.assignedSlots[section.slotList[index].slotId] = newFieldKey;
  //         if (lastFieldKey) {
  //           this.returnItemToAvailableList(lastFieldKey);
  //         }
  //       }
  //     }
  //     return;
  //   })
  // }

  insertFieldKeyIntoSection(sectionName: string, targetSlot: string, newFieldKey: string) {
    // Guard against missing structures
    if (!Array.isArray(this.templateLayout)) return;

    // Ensure assignedSlots exists
    this.assignedSlots ??= {};

    for (const section of this.templateLayout as ILayoutSection[]) {
      if (section.sectionName !== sectionName) continue;

      const slots = section.slotList ?? [];
      const index = slots.findIndex(slot => slot?.slotId === targetSlot);
      if (index === -1) {
        // Slot not found in this section; nothing to do
        return;
      }

      const target = slots[index];
      if (!target) {
        // Defensive: index found but slot missing
        return;
      }

      const currentKey = target.fieldKey ?? '';

      if (currentKey === '') {
        // Slot is free, just assign the field key
        target.fieldKey = newFieldKey;
        this.assignedSlots[targetSlot] = newFieldKey;
        return; // done
      } else {
        // Shift field keys down from the target slot to the end
        const maxIndex = slots.length - 1;
        const lastSlot = slots[maxIndex];
        const lastFieldKey = lastSlot?.fieldKey ?? '';

        for (let i = maxIndex; i > index; i--) {
          const dst = slots[i];
          const src = slots[i - 1];
          if (!dst || !src) continue; // defensive

          dst.fieldKey = src.fieldKey ?? '';
          const dstId = dst.slotId ?? '';
          const srcKey = src.fieldKey ?? '';
          if (dstId) {
            this.assignedSlots[dstId] = srcKey;
          }
        }

        // Insert new key in target slot
        target.fieldKey = newFieldKey;
        const targetId = target.slotId ?? '';
        if (targetId) {
          this.assignedSlots[targetId] = newFieldKey;
        }

        // If we pushed a real key off the end, return it to available list
        if (lastFieldKey) {
          this.returnItemToAvailableList(lastFieldKey);
        }
        return; // done
      }
    }
  }


}


